import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { TextField, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OrderingCheckbox from './OrderingCheckbox';
import { extractStyleNo, orderCogsTotal } from '../../utils/sizeGrids';
import { formatCurrency } from '../../utils/formatters';

const Sidebar = styled.aside`
  background: var(--al-surface);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 400px;
  max-height: calc(100vh - 280px);
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.12em;
`;

const BulkActions = styled.div`
  display: flex;
  gap: 8px;
`;

const OrdersScroll = styled.div`
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px;
`;

const OrderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OrderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  min-height: 40px;

  &:hover {
    border-color: rgba(var(--al-primary-rgb), 0.25);
    background: rgba(255, 255, 255, 0.03);
  }
`;

const LineRow = styled(OrderRow)`
  margin-left: 12px;
  background: transparent;
  border-color: transparent;
  padding: 8px 10px;
  min-height: 36px;

  &:hover {
    border-color: rgba(var(--al-primary-rgb), 0.15);
    background: rgba(255, 255, 255, 0.02);
  }
`;

const OrderLabel = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.35;
`;

const CustomerName = styled.div`
  font-weight: 600;
  font-size: 0.85rem;
`;

const OrderMeta = styled.div`
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 2px;
`;

const LineCount = styled.span`
  flex-shrink: 0;
  background: var(--al-primary);
  color: var(--al-bg);
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
`;

const DesignName = styled.span`
  font-size: 0.85em;
  font-weight: 600;
`;

const CogsSuffix = styled.span`
  color: rgba(var(--al-primary-rgb), 0.85);
  font-weight: 500;
`;

const LineMeta = styled.span`
  font-size: 0.82em;
  color: rgba(255, 255, 255, 0.65);
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
`;

function matchesSearch(order, lines, searchLower) {
  if (!searchLower) return true;
  if (order.customerName?.toLowerCase().includes(searchLower)) return true;
  if (order.orderNumber?.toLowerCase().includes(searchLower)) return true;
  return lines.some(
    (line) =>
      line.designName?.toLowerCase().includes(searchLower) ||
      line.garmentId?.toLowerCase().includes(searchLower) ||
      line.garmentColor?.toLowerCase().includes(searchLower)
  );
}

function OrderingSidebar({
  orders,
  linesByOrder,
  products,
  selectedLines,
  searchTerm,
  onSearchChange,
  onOrderCheck,
  onLineCheck,
  onSelectAll,
  onClearSelection,
  isOrderChecked,
  isOrderIndeterminate,
}) {
  const searchLower = searchTerm.trim().toLowerCase();

  const filteredOrders = useMemo(
    () =>
      orders.filter((order) => {
        const lines = linesByOrder[order.id] || [];
        return matchesSearch(order, lines, searchLower);
      }),
    [orders, linesByOrder, searchLower]
  );

  const allVisibleLineIds = useMemo(
    () =>
      filteredOrders.flatMap((order) => (linesByOrder[order.id] || []).map((line) => line.id)),
    [filteredOrders, linesByOrder]
  );

  const allVisibleSelected =
    allVisibleLineIds.length > 0 && allVisibleLineIds.every((id) => selectedLines[id]);

  return (
    <Sidebar>
      <SidebarHeader>
        <SectionTitle>Ready to order</SectionTitle>
        <BulkActions>
          <Button
            size="small"
            onClick={onSelectAll}
            disabled={allVisibleLineIds.length === 0 || allVisibleSelected}
            sx={{ fontSize: '0.65rem', minWidth: 0, px: 1 }}
          >
            All
          </Button>
          <Button
            size="small"
            onClick={onClearSelection}
            disabled={Object.values(selectedLines).every((v) => !v)}
            sx={{ fontSize: '0.65rem', minWidth: 0, px: 1 }}
          >
            Clear
          </Button>
        </BulkActions>
      </SidebarHeader>

      <TextField
        placeholder="Search customer, design, style..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        size="small"
        fullWidth
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.5)', fontSize: 20 }} />,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            color: 'white',
            fontSize: '0.85rem',
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
            '&.Mui-focused fieldset': { borderColor: 'var(--al-primary)' },
          },
        }}
      />

      <OrdersScroll>
        {filteredOrders.length === 0 ? (
          <EmptyMessage>
            {searchLower ? 'No matches for your search.' : 'No orders ready to order.'}
          </EmptyMessage>
        ) : (
          filteredOrders.map((order) => {
            const lines = linesByOrder[order.id] || [];
            const visibleLines = searchLower
              ? lines.filter((line) =>
                  matchesSearch(order, [line], searchLower)
                )
              : lines;

            if (visibleLines.length === 0) return null;

            const orderCogs = orderCogsTotal(lines, products);

            return (
              <OrderBlock key={order.id}>
                <OrderRow onClick={() => onOrderCheck(order.id, !isOrderChecked(order.id))}>
                  <OrderingCheckbox
                    checked={isOrderChecked(order.id)}
                    indeterminate={isOrderIndeterminate(order.id)}
                    onToggle={() => onOrderCheck(order.id, !isOrderChecked(order.id))}
                  />
                  <OrderLabel>
                    <CustomerName>
                      {order.customerName || 'Unknown Customer'}
                      {orderCogs > 0 && (
                        <CogsSuffix> — {formatCurrency(orderCogs)}</CogsSuffix>
                      )}
                    </CustomerName>
                    {order.orderNumber && (
                      <OrderMeta>#{order.orderNumber}</OrderMeta>
                    )}
                  </OrderLabel>
                  <LineCount>{lines.length}</LineCount>
                </OrderRow>

                {visibleLines.map((line) => {
                  const styleNo = extractStyleNo(line.garmentId);
                  const checked = !!selectedLines[line.id];
                  return (
                    <LineRow
                      key={line.id}
                      onClick={() => onLineCheck(line.id, !checked)}
                    >
                      <OrderingCheckbox
                        checked={checked}
                        onToggle={() => onLineCheck(line.id, !checked)}
                      />
                      <OrderLabel>
                        <DesignName>{line.designName}</DesignName>
                        <br />
                        <LineMeta>
                          {styleNo} — {line.garmentColor}
                        </LineMeta>
                      </OrderLabel>
                    </LineRow>
                  );
                })}
              </OrderBlock>
            );
          })
        )}
      </OrdersScroll>
    </Sidebar>
  );
}

export default OrderingSidebar;
